import type { Chosen, SourceEntry } from '../../api/connections'

/**
 * A path in a foreign system, handled the way this side has to handle it: as an identity.
 *
 * These are the same rules the core states — segments joined with `/`, no slash at either end, the
 * empty string for the top of what an account can see — written here as pure functions so that the
 * screen can answer "am I inside something that is already ticked" without a request. Reimplemented
 * rather than shared, because the two sides are a network apart, and **the service decides**: nothing
 * here refuses a path or repairs one beyond dropping the slashes somebody typed too many of. A path
 * that does not exist is an answer the other system gives, and getting that answer straight away is
 * worth more than a guess made here.
 */

/** The top of what these credentials can see. Not a folder somebody chose. */
export const ROOT = ''

/** What the last segment says, or the empty string for {@link ROOT}, which is nobody's name. */
export function nameOf(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1)
}

/**
 * How a path is said in the middle of a sentence — a control's name, a line about what covers what.
 *
 * The root is the one path with nothing to read out: it is the empty string, and a control called
 * "Untick" with nothing after it names nothing. So it is said in words instead.
 */
export function spoken(path: string): string {
  return path === ROOT ? 'everything you can see' : path
}

/** Every step from the top down to this path, each as the full path it is. Empty for {@link ROOT}. */
export function stepsTo(path: string): string[] {
  if (path === ROOT) {
    return []
  }
  const steps: string[] = []
  let walked = ROOT
  for (const segment of path.split('/')) {
    walked = walked === ROOT ? segment : `${walked}/${segment}`
    steps.push(walked)
  }
  return steps
}

/**
 * Whether `folder` is `path` or lies above it.
 *
 * {@link ROOT} holds everything, which is why ticking it is ticking the whole account — the one tick
 * somebody can make that needs no walking at all.
 */
export function holds(folder: string, path: string): boolean {
  return folder === ROOT || path === folder || path.startsWith(`${folder}/`)
}

/**
 * The ticked folder this one already lies inside, or `null` when there is none.
 *
 * A folder that is itself ticked is not covered by itself: it is a choice somebody made and can take
 * back, and showing it as "already coming" would hide the only control that stops it.
 */
export function coveringOf(chosen: readonly Chosen[], path: string): Chosen | null {
  return chosen.find((one) => one.path !== path && holds(one.path, path)) ?? null
}

export function isChosen(chosen: readonly Chosen[], path: string): boolean {
  return chosen.some((one) => one.path === path)
}

/**
 * A path as somebody typed it, with the slashes they used too many of taken out.
 *
 * Leading, trailing and repeated slashes go, and so does the whitespace around a segment — those are
 * ways of writing the same path, not different paths. Anything else is left exactly as typed: a
 * segment is what the other system called it, and tidying one would point this at something its text
 * does not say.
 */
export function tidy(raw: string): string {
  return raw
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '')
    .join('/')
}

/**
 * One level, in the order it is read in: folders first, then files, each by name.
 *
 * The other system gives them in whatever order it pleases, and the two kinds are here for different
 * reasons — the folders are what somebody is choosing between, the files are only evidence of what a
 * folder holds. Mixed together, the thing being decided would be scattered through the thing being
 * glanced at.
 */
export function arranged(entries: readonly SourceEntry[]): SourceEntry[] {
  return [...entries].sort((one, other) =>
    one.folder === other.folder
      ? one.name.localeCompare(other.name)
      : Number(other.folder) - Number(one.folder),
  )
}
